import { forwardRef } from "react";
import { Archive, CalendarClock, SendHorizontal } from "lucide-react";
import { BACKUP_FOLDER, BACKUP_LABEL, SYSTEM_SENT_FOLDER, SYSTEM_SENT_LABEL } from "../../api/mailbox";
import { Badge } from "../../components/Badge";
import type { MailBackupUsage, MailFolder } from "../../api/types";
import { usageLine } from "./backupUsage";

// 左欄:資料夾 + 「已排程」。手機(< 768px)上它是單欄流程的第一步,`className` 由頁面決定顯示與否。

export interface MailFolderNavProps {
  /** 沒啟用信箱時是空的:只剩「系統寄出」(啟用前也看得到系統替你寄的信)。 */
  folders: MailFolder[];
  current: string;
  /** 「已排程」那一頁開著。 */
  scheduledOpen: boolean;
  className: string;
  onChoose: (name: string) => void;
  /** 沒給 = 不畫「已排程」(沒啟用信箱就沒有排程)。 */
  onScheduled?: () => void;
  /** 系統備份的用量(還沒讀到是 null / 不給)。 */
  backupUsage?: MailBackupUsage | null;
  /** 沒啟用信箱就沒有備份可看(預設有)。 */
  showBackup?: boolean;
}

const item = (active: boolean) =>
  "flex w-full items-center gap-2 rounded-[var(--radius)] px-3 py-2 text-left text-sm " +
  (active ? "bg-[var(--accent-soft)] text-[var(--accent-hover)]" : "text-[var(--text-2)] hover:bg-[var(--surface-2)]");

export const MailFolderNav = forwardRef<HTMLElement, MailFolderNavProps>(function MailFolderNav(
  { folders, current, scheduledOpen, className, onChoose, onScheduled, backupUsage, showBackup = true },
  ref,
) {
  return (
    <nav ref={ref} aria-label="資料夾" className={className}>
      <ul className="m-0 flex flex-col gap-1 p-0 md:flex-row md:flex-wrap lg:flex-col">
        {folders.map((one) => {
          const active = !scheduledOpen && current === one.name;
          return (
            <li key={one.name} className="list-none">
              <button
                type="button"
                aria-current={active ? "page" : undefined}
                aria-label={one.unread > 0 ? `${one.label},${one.unread} 封未讀` : one.label}
                onClick={() => onChoose(one.name)}
                className={item(active)}
              >
                <span className="flex-1">{one.label}</span>
                {one.unread > 0 ? <Badge tone="accent">{one.unread}</Badge> : null}
              </button>
            </li>
          );
        })}
        {onScheduled ? (
          <li className="list-none">
            <button
              type="button"
              aria-current={scheduledOpen ? "page" : undefined}
              onClick={onScheduled}
              className={item(scheduledOpen)}
            >
              <CalendarClock aria-hidden="true" size={14} />
              <span className="flex-1">已排程</span>
            </button>
          </li>
        ) : null}
        {/* 系統寄出:系統替本人寄的信(報價單、回簽信),唯讀、只有本人;緊貼在系統備份上面 */}
        <li className="list-none">
          <button
            type="button"
            aria-current={!scheduledOpen && current === SYSTEM_SENT_FOLDER ? "page" : undefined}
            onClick={() => onChoose(SYSTEM_SENT_FOLDER)}
            className={item(!scheduledOpen && current === SYSTEM_SENT_FOLDER)}
          >
            <SendHorizontal aria-hidden="true" size={14} />
            <span className="flex-1">{SYSTEM_SENT_LABEL}</span>
          </button>
        </li>
        {/* 系統備份:全部備份的信(含雲端已刪除),唯讀;固定放在最下面。沒啟用信箱就沒有 */}
        {showBackup ? (
          <li className="list-none">
            <button
              type="button"
              aria-current={!scheduledOpen && current === BACKUP_FOLDER ? "page" : undefined}
              onClick={() => onChoose(BACKUP_FOLDER)}
              className={item(!scheduledOpen && current === BACKUP_FOLDER)}
            >
              <Archive aria-hidden="true" size={14} />
              <span className="flex-1">{BACKUP_LABEL}</span>
            </button>
            {backupUsage ? (
              <span className="block px-3 pb-1 pl-[2.1rem] text-xs text-[var(--text-3)]">{usageLine(backupUsage)}</span>
            ) : null}
          </li>
        ) : null}
      </ul>
    </nav>
  );
});
